
import { Injector } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';

import { AppComponentBase as BaseAppComponentBase } from '@axilla/axilla-shared';

import { WebshopService } from '@app/shared/common/shop-system/services/webshop.service';

import { SelectItem } from 'primeng/api';
import {BasketArticleExtendedDto} from '@app/shared/common/shop-system/models/models';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import {PermissionCheckerService} from '@node_modules/abp-ng2-module';
import {FeatureDefinition} from '@shared/features/base-features-definitions';
import {BasketArticleDto} from '@shared/service-proxies/service-proxies';

export abstract class AppComponentBase extends BaseAppComponentBase   {
    remoteServiceBaseUrl: string = AppConsts.remoteServiceBaseUrl;

    localizationSourceName = AppConsts.localization.defaultLocalizationSourceName;

    protected readonly defaultQuickAddAmounts: number[] = [10, 50, 100, 200, 500];

    protected webshopService: WebshopService;
    protected permissionChecker: PermissionCheckerService;

    sortOptions: SelectItem[];

    rowsAmount = 12;

    get isGuided(): boolean {
        const guidedUser = this.permissionChecker.isGranted(BasePermissionDefinition.Webshop.View.Guided);
        return guidedUser && !!abp.session.impersonatorUserId;
    }

    get isLegacyUI(): boolean {
        return this.webshopService.isLegacyUi;
    }

    constructor(injector: Injector) {
        super(injector);

        this.webshopService = injector.get(WebshopService);
        this.permissionChecker = injector.get(PermissionCheckerService);

        this.sortOptions = [
            { label: 'Sort by relevance', value: 'relevance' },
            { label: 'Price (high to low)', value: '!price' },
            { label: 'Price (low to high)', value: 'price' }
        ];
    }

    get basket(): BasketArticleExtendedDto[] {
        return this.webshopService.basket;
    }

    showIsBlockedMessage() {
        let title = this.localization.localize('Axilla.App.Webshop.UserIsBlocked', 'Axilla');
        let message = this.localization.localize('Axilla.App.Webshop.UserIsBlockedMessage', 'Axilla');

        abp.message.warn(message, title);
    }

    get isOuBlocked() {
        return this.webshopService.isOuBlocked;
    }

    checkIsOuBlocked() {
        this.webshopService.isBlocked$()
            .subscribe((result) => {
                if (this.isOuBlocked) {
                    this.showIsBlockedMessage();
                }
            });
    }

    getCurrency(currency?: any) {
        if (currency != null) {
            return this.webshopService.getCurrency(+currency);
        }

        return this.webshopService.getCurrency();
    }

    getImgUrl(selectedItemCode: string) {
        return `${this.remoteServiceBaseUrl}/WebShop/${this.appSession.tenant.id}/GetArticleImage/${selectedItemCode}.jpg`;
    }

    preventClick($event: Event) {
        $event.stopPropagation();
    }

    isStockAvailable(articleDto: any): boolean {
        return articleDto.availableAmount <= 0;
    }

    isGapAvailable(articleDto: any): boolean {
        return articleDto.availableAmount > 0;
    }

    get isBackOrderFeatureEnabled(): boolean {
        return abp.features.isEnabled(FeatureDefinition.App.Backorder);
    }

    isInBasket(article: BasketArticleDto): boolean {
        return !!this.webshopService.basket.find((item) => item.id === article?.id);
    }

    getBasketAmount(article: BasketArticleDto): number {
        const item = this.webshopService.basket.find((item) => item.id === article.id) as BasketArticleExtendedDto;

        if (item.basketAmount !== item.backorderQuantity + item.quantity) {
            return (item.backorderQuantity || 0) + item.quantity || 0;
        }

        return item.basketAmount || 0;
    }

    getQuickAddAmounts(article: BasketArticleDto): number[] {
        if (!article?.advisedAmount) {
            return this.defaultQuickAddAmounts;
        }
        return article.advisedAmount;
    }

    protected resetQuantity(article: BasketArticleDto) {
        article.quantity = 1;
    }
}

